import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { getCompletedReviews } from '@/api/widget'
import { CompletedReviewsResponse, hydrateTerpliProductReview } from '@/types/widget'
import { getCurrentGlobalID, getCurrentMenuType, useSessionStore } from './useSessionStore'
import { logger, showErrorToast } from '@/utils'
import { track } from '@/utils/analytics'
import { classifyReviewError } from '@/utils/reviewErrors'
import { withRefreshRetry } from '@/utils/reviewAuth'
import { getStoreIDByGlobalID } from './useLocationsStore'

type CompletedReviewsStoreState = {
  loading: boolean
  loaded: boolean
  completedReviews: CompletedReviewsResponse | undefined
  reviewsDisabled: boolean
  error: string | undefined
}

type CompletedReviewsStoreActions = {
  initialize: () => Promise<void>
  fetch: (force?: boolean) => Promise<CompletedReviewsResponse | undefined>
  reset: () => void
}

type CompletedReviewsStore = CompletedReviewsStoreState & CompletedReviewsStoreActions

const initialState: CompletedReviewsStoreState = {
  loading: false,
  loaded: false,
  completedReviews: undefined,
  reviewsDisabled: false,
  error: undefined,
}

export const useCompletedReviewsStore = create<CompletedReviewsStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetch: async (force: boolean = false) => {
        if (!get().loaded && get().loading && !force) {
          return undefined
        }

        const { currentUID, reviewToken, reviewPII } = useSessionStore.getState()
        if (!currentUID) {
          showErrorToast('No current UID set')
          return undefined
        }
        if (!reviewToken || !reviewPII) {
          return undefined
        }

        set(
          {
            loading: true,
            loaded: false,
            completedReviews: undefined,
            reviewsDisabled: false,
            error: undefined,
          },
          false,
          'fetch/start'
        )

        let completedReviews: CompletedReviewsResponse | undefined
        try {
          const globalID = getCurrentGlobalID()
          const [result, error] = await withRefreshRetry((tok) =>
            getCompletedReviews(currentUID, reviewPII, tok, {
              storeID: globalID ? getStoreIDByGlobalID(globalID) : undefined,
              menuType: getCurrentMenuType(),
            })
          )
          completedReviews = result

          if (error) {
            logger.error('fetchCompletedReviews', error)
            const cls = classifyReviewError(error)
            if (cls === 'contactNotFound') {
              set(
                { loading: false, loaded: true, completedReviews: undefined, error: undefined },
                false,
                'fetch/noContact'
              )
              return undefined
            }
            set(
              {
                loading: false,
                loaded: true,
                completedReviews: undefined,
                reviewsDisabled: cls === 'reviewsDisabled',
                error,
              },
              false,
              'fetch/error'
            )
            return undefined
          }

          // Same hydration as pending: fill `product_uid` from BE `productId`,
          // safe defaults for rec-only fields BE doesn't return today.
          const hydrated: CompletedReviewsResponse | undefined = completedReviews
            ? completedReviews.map(hydrateTerpliProductReview)
            : undefined

          logger.log('fetchCompletedReviews', { completedReviews: hydrated })

          set(
            {
              loading: false,
              loaded: true,
              completedReviews: hydrated,
              reviewsDisabled: false,
              error: undefined,
            },
            false,
            'fetch/success'
          )
          return hydrated
        } finally {
          track('review', 'list_reviews', {
            reviews_size: (completedReviews ?? []).length,
          })
        }
      },

      initialize: async () => {
        await get().fetch()
      },

      reset: () => {
        set(initialState, false, 'reset')
      },
    }),
    { name: 'CompletedReviewsStore', enabled: process.env.NODE_ENV === 'development' }
  )
)
